import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useCallback, useState } from 'react'

interface ConfirmOptions {
  title: string
  message: string
  singleAction?: boolean
  destructive?: boolean
}

export const useConfirm = ({
  title,
  message,
  singleAction = false,
  destructive = false,
}: ConfirmOptions) => {
  const [resolver, setResolver] = useState<{
    resolve: (value: boolean) => void
  } | null>(null)

  const close = useCallback(() => {
    setResolver(null)
  }, [])

  const confirm = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      setResolver({ resolve })
    })
  }, [])

  const handleConfirm = useCallback(() => {
    resolver?.resolve(true)
    close()
  }, [resolver, close])

  const handleCancel = useCallback(() => {
    resolver?.resolve(false)
    close()
  }, [resolver, close])

  const ConfirmationDialog = useCallback(
    () => (
      <Dialog
        open={resolver !== null}
        onClose={singleAction ? handleConfirm : handleCancel}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>

        <DialogActions>
          {singleAction ? (
            <Button variant='contained' onClick={handleConfirm}>
              OK
            </Button>
          ) : (
            <>
              <Button onClick={handleCancel}>Cancel</Button>

              <Button
                variant='contained'
                color={destructive ? 'error' : 'primary'}
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    ),
    [resolver, title, message, singleAction, destructive, handleCancel, handleConfirm],
  )

  return {
    confirm,
    dialog: ConfirmationDialog,
  }
}
